import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import { toast } from "react-toastify";
import { BASE_URL } from "../../constants";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import { FILE_RULES, validateFile } from "../../utils/fileValidation";

// Worker config
// Serve the worker from this app so PDF parsing does not depend on a runtime CDN.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const inputStyle = {
  width: "100%",
  height: "38px",
  padding: "5px 8px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  outline: "none",
  fontSize: "14px",
};
export default function PdfLayoutGuide({
  yValue,
  patientInfoLeftX,
  setPatientInfoLeftX,
  patientInfoLeftY,
  setPatientInfoLeftY,
  patientInfoRightX,
  setPatientInfoRightX,
  patientInfoRightY,
  setPatientInfoRightY,
  setYValue,
  doctorInfo,
  setDoctorInfo,
  doctorX,
  setDoctorX,
  doctorY,
  setDoctorY,
  doctorNote,
  setDoctorNote,
  appNote,
  setAppNote,
  noteX,
  setNoteX,
  noteY,
  setNoteY,
  selectedDoctor,
  setSelectedDoctor,
  setDoctors,
  doctors,
  handleCloseSettings,
  orgPDF,
}) {
  // file + page viewport (real PDF pixels)
  const [file, setFile] = useState(null);
  const [viewport, setViewport] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showVitals, setShowVitals] = useState(null);
  const [prescriptionBox, setPrescriptionBox] = useState({
    x: 10,
    y: 280,
    width: 580,
    height: 400,
  });

  // Zoom (visual-only). 1 = 100%
  const [zoom, setZoom] = useState(1);

  // Snapping config
  const SNAP_BASE = 10; // default snapping in px
  const SNAP_FINE = 1; // with Shift

  // Dragging state
  const [dragging, setDragging] = useState(null); // "patient" | "doctor" | "note" | null
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // offset in *screen pixels* used when dragging

  // Helper: snap value to grid
  const snapValue = (val, fine) => {
    const snap = fine ? SNAP_FINE : SNAP_BASE;
    return Math.round(val / snap) * snap;
  };

  const loadPdfFromUrl = async (url) => {
    const pdf = await pdfjsLib?.getDocument(url)?.promise;
    const page = await pdf?.getPage(1);
    const vp = page?.getViewport({ scale: 1 });

    setViewport(vp);

    const canvas = document?.getElementById("pdf-canvas");
    const ctx = canvas?.getContext("2d");

    canvas.width = vp?.width;
    canvas.height = vp?.height;

    await page?.render({ canvasContext: ctx, viewport: vp })?.promise;

    canvas.style.width = `${vp?.width * zoom}px`;
    canvas.style.height = `${vp?.height * zoom}px`;
  };

  useEffect(() => {
    if (selectedDoctor?.whichAppointmentTemplate === "org") {
      loadPdfFromUrl(orgPDF);
      setFile(null);
    } else if (selectedDoctor?.whichAppointmentTemplate === "doctor") {
      if (selectedDoctor?.appointmentTemplate?.pdfUrl) {
        loadPdfFromUrl(selectedDoctor?.appointmentTemplate?.pdfUrl);
        setFile(null);
      } else {
        setFile(null);
      }
    } else {
      setFile(null);
    }

    setPatientInfoLeftX(
      Number(
        selectedDoctor?.appointmentTemplate?.patientInfoLeftX === undefined
          ? patientInfoLeftX
          : selectedDoctor?.appointmentTemplate?.patientInfoLeftX,
      ),
    );
    setPatientInfoLeftY(
      Number(
        selectedDoctor?.appointmentTemplate?.patientInfoLeftY === undefined
          ? patientInfoLeftY
          : selectedDoctor?.appointmentTemplate?.patientInfoLeftY,
      ),
    );
    setPatientInfoRightX(
      Number(
        selectedDoctor?.appointmentTemplate?.patientInfoRightX === undefined
          ? patientInfoRightX
          : selectedDoctor?.appointmentTemplate?.patientInfoRightX,
      ),
    );
    setPatientInfoRightY(
      Number(
        selectedDoctor?.appointmentTemplate?.patientInfoRightY === undefined
          ? patientInfoRightY
          : selectedDoctor?.appointmentTemplate?.patientInfoRightY,
      ),
    );
    setDoctorInfo(
      selectedDoctor?.appointmentTemplate?.doctorInfo === undefined
        ? doctorInfo
        : selectedDoctor?.appointmentTemplate?.doctorInfo,
    );
    setDoctorX(
      Number(
        selectedDoctor?.appointmentTemplate?.doctorX === undefined
          ? doctorX
          : selectedDoctor?.appointmentTemplate?.doctorX,
      ),
    );
    setDoctorY(
      Number(
        selectedDoctor?.appointmentTemplate?.doctorY === undefined
          ? doctorY
          : selectedDoctor?.appointmentTemplate?.doctorY,
      ),
    );
    setDoctorNote(
      selectedDoctor?.appointmentTemplate?.doctorNote === undefined
        ? doctorNote
        : selectedDoctor?.appointmentTemplate?.doctorNote,
    );
    setAppNote(
      selectedDoctor?.appointmentTemplate?.appNote === undefined
        ? appNote
        : selectedDoctor?.appointmentTemplate?.appNote,
    );
    setNoteX(
      Number(
        selectedDoctor?.appointmentTemplate?.noteX === undefined
          ? noteX
          : selectedDoctor?.appointmentTemplate?.noteX,
      ),
    );
    setNoteY(
      Number(
        selectedDoctor?.appointmentTemplate?.noteY === undefined
          ? noteY
          : selectedDoctor?.appointmentTemplate?.noteY,
      ),
    );

    const apiShowVitals = selectedDoctor?.appointmentTemplate?.showVitals;
    setShowVitals(
      apiShowVitals === false || apiShowVitals === "false" ? false : true,
    );

    setPrescriptionBox((prev) => ({
      ...prev,
      x:
        selectedDoctor?.appointmentTemplate?.prescriptionX === undefined
          ? prev.x
          : Number(selectedDoctor?.appointmentTemplate?.prescriptionX),

      y:
        selectedDoctor?.appointmentTemplate?.prescriptionY === undefined
          ? prev.y
          : Number(selectedDoctor?.appointmentTemplate?.prescriptionY),

      width:
        selectedDoctor?.appointmentTemplate?.prescriptionWidth === undefined
          ? prev.width
          : Number(selectedDoctor?.appointmentTemplate?.prescriptionWidth),

      height:
        selectedDoctor?.appointmentTemplate?.prescriptionHeight === undefined
          ? prev.height
          : Number(selectedDoctor?.appointmentTemplate?.prescriptionHeight),
    }));
  }, []);

  // Load PDF and set viewport (real pixels)
  const handlePdfUpload = async (e) => {
    const uploaded = e.target.files[0];
    if (!uploaded) return;
    const validation = await validateFile(uploaded, FILE_RULES.pdf);
    if (!validation.valid) {
      alert(validation.error);
      e.target.value = "";
      return;
    }
    setFile(uploaded);

    const buffer = await uploaded.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const page = await pdf.getPage(1);
    // use scale 1 to get real PDF pixel dimensions
    const vp = page.getViewport({ scale: 1 });
    setViewport(vp);

    const canvas = document.getElementById("pdf-canvas");
    const ctx = canvas.getContext("2d");

    // set the canvas internal pixel dimensions to real PDF pixels
    canvas.width = vp.width;
    canvas.height = vp.height;

    // render the page into the canvas (unscaled)
    await page.render({ canvasContext: ctx, viewport: vp }).promise;

    // ensure canvas CSS size reflects initial zoom
    canvas.style.width = `${vp.width * zoom}px`;
    canvas.style.height = `${vp.height * zoom}px`;
  };

  // Start dragging: compute offset relative to element top-left in screen pixels
  const startDrag = (e, type, currentXUnscaled, currentYUnscaled) => {
    if (!viewport) return;
    const wrapper = document.getElementById("canvasWrapper");
    const rect = wrapper.getBoundingClientRect();

    // currentXUnscaled/currentYUnscaled are real PDF pixels; convert to screen (scaled) pixels
    const currentXScaled = currentXUnscaled * zoom;
    const currentYScaled = currentYUnscaled * zoom;

    // offset = mouse position inside the overlay (in screen px)
    const offsetX = e.clientX - rect.left - currentXScaled;
    const offsetY = e.clientY - rect.top - currentYScaled;

    setDragging(type);
    setOffset({ x: offsetX, y: offsetY });

    // prevent text selection while dragging
    e.preventDefault();
  };

  // Handle mouse move while dragging - compute new unscaled coords and set state (snapped/clamped)
  const handleMouseMove = (e) => {
    if (!dragging || !viewport) return;

    const wrapper = document.getElementById("canvasWrapper");
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();

    // Mouse position inside wrapper, in screen pixels
    const mouseXInWrapper = e.clientX - rect.left;
    const mouseYInWrapper = e.clientY - rect.top;

    // Convert to unscaled PDF pixels (since visual is scaled by zoom)
    let newXUnscaled = (mouseXInWrapper - offset.x) / zoom;
    let newYUnscaled = (mouseYInWrapper - offset.y) / zoom;

    // Choose snap size: default snap base, hold Shift for fine snap
    const fine = e.shiftKey === true;
    newXUnscaled = snapValue(newXUnscaled, fine);
    newYUnscaled = snapValue(newYUnscaled, fine);

    // clamp within PDF bounds (overlay box size considered)
    // We'll use overlay approximate sizes to avoid overflow when dragging:
    const patientOverlayHeight = 24; // approximate height (single-line)
    const doctorBoxW = 250;
    const doctorBoxH = 115;
    const noteBoxW = 308;
    const noteBoxH = 18;

    if (dragging === "patient") {
      // patient overlay spans full width; only Y needs clamping
      const minY = 0;
      const maxY = Math.max(0, viewport.height - patientOverlayHeight);
      newYUnscaled = Math.max(minY, Math.min(newYUnscaled, maxY));
      setYValue(Math.round(newYUnscaled));
    } else if (dragging === "doctor") {
      const minX = 0;
      const maxX = Math.max(0, viewport.width - doctorBoxW);
      const minY = 0;
      const maxY = Math.max(0, viewport.height - doctorBoxH);
      newXUnscaled = Math.max(minX, Math.min(newXUnscaled, maxX));
      newYUnscaled = Math.max(minY, Math.min(newYUnscaled, maxY));
      setDoctorX(Math.round(newXUnscaled));
      setDoctorY(Math.round(newYUnscaled));
    } else if (dragging === "note") {
      const minX = 0;
      const maxX = Math.max(0, viewport.width - noteBoxW);
      const minY = 0;
      const maxY = Math.max(0, viewport.height - noteBoxH);
      newXUnscaled = Math.max(minX, Math.min(newXUnscaled, maxX));
      newYUnscaled = Math.max(minY, Math.min(newYUnscaled, maxY));
      setNoteX(Math.round(newXUnscaled));
      setNoteY(Math.round(newYUnscaled));
    } else if (dragging === "patientLeft") {
      const boxW = 180; // your left box width
      const boxH = 40; // height
      const minX = 0;
      const maxX = viewport.width - boxW;
      const minY = 0;
      const maxY = viewport.height - boxH;

      newXUnscaled = Math.max(minX, Math.min(newXUnscaled, maxX));
      newYUnscaled = Math.max(minY, Math.min(newYUnscaled, maxY));

      setPatientInfoLeftX(Math.round(newXUnscaled));
      setPatientInfoLeftY(Math.round(newYUnscaled));
    } else if (dragging === "patientRight") {
      const boxW = 180;
      const boxH = 40;
      const minX = 0;
      const maxX = viewport.width - boxW;
      const minY = 0;
      const maxY = viewport.height - boxH;

      newXUnscaled = Math.max(minX, Math.min(newXUnscaled, maxX));
      newYUnscaled = Math.max(minY, Math.min(newYUnscaled, maxY));

      setPatientInfoRightX(Math.round(newXUnscaled));
      setPatientInfoRightY(Math.round(newYUnscaled));
    } else if (dragging === "prescription-container") {
      const boxW = prescriptionBox.width;
      const boxH = prescriptionBox.height;

      const minX = 0;
      const maxX = viewport.width - boxW;
      const minY = 0;
      const maxY = viewport.height - boxH;

      newXUnscaled = Math.max(minX, Math.min(newXUnscaled, maxX));
      newYUnscaled = Math.max(minY, Math.min(newYUnscaled, maxY));

      setPrescriptionBox((prev) => ({
        ...prev,
        x: Math.round(newXUnscaled),
        y: Math.round(newYUnscaled),
      }));
    } else if (dragging === "prescription-resize") {
      const dx = (e.clientX - offset.x) / zoom;
      const dy = (e.clientY - offset.y) / zoom;
      setOffset({ x: e.clientX, y: e.clientY });
      setPrescriptionBox((prev) => ({
        ...prev,
        width: Math.max(450, prev.width + dx),
        height: Math.max(400, prev.height + dy),
      }));
    }
  };

  const stopDrag = () => {
    setDragging(null);
  };

  // When zoom changes, update canvas CSS dimensions and wrapper dims
  const handleZoomChange = (value) => {
    const newZoom = Number(value);
    setZoom(newZoom);
    const canvas = document.getElementById("pdf-canvas");
    if (canvas && viewport) {
      canvas.style.width = `${viewport.width * newZoom}px`;
      canvas.style.height = `${viewport.height * newZoom}px`;
      // wrapper will use viewport.width * zoom in style below automatically
    }
  };

  // Utility: clamp input edits to viewport bounds and snap
  const clampAndSnapInput = (x, y, boxW = 0, boxH = 0, fine = false) => {
    if (!viewport) return { x, y };
    const maxX = Math.max(0, viewport.width - boxW);
    const maxY = Math.max(0, viewport.height - boxH);
    let nx = Math.round(Math.max(0, Math.min(x, maxX)));
    let ny = Math.round(Math.max(0, Math.min(y, maxY)));
    nx = snapValue(nx, fine);
    ny = snapValue(ny, fine);
    return { x: nx, y: ny };
  };

  const onPatientYInputChange = (ny) => {
    const { y } = clampAndSnapInput(0, ny, 0, 24, false);
    setYValue(y);
  };

  // patient info single-line text (preview only)
  const patientInfoLine = "Patient Info";

  const handleSubmitPdfSettings = async () => {
    try {
      setLoading(true);
      const formData = new FormData();

      formData.append("appointmentFile", file);
      formData.append("patientInfoLeftX", patientInfoLeftX);
      formData.append("patientInfoLeftY", patientInfoLeftY);
      formData.append("patientInfoRightX", patientInfoRightX);
      formData.append("patientInfoRightY", patientInfoRightY);
      formData.append("doctorInfo", doctorInfo);
      formData.append("doctorX", doctorX);
      formData.append("doctorY", doctorY);
      formData.append("doctorNote", doctorNote);
      formData.append("appNote", appNote);
      formData.append("noteX", noteX);
      formData.append("noteY", noteY);
      formData.append("showVitals", showVitals);
      formData.append("prescriptionX", prescriptionBox.x);
      formData.append("prescriptionY", prescriptionBox.y);
      formData.append("prescriptionWidth", prescriptionBox.width);
      formData.append("prescriptionHeight", prescriptionBox.height);

      const response = await fetch(
        `${BASE_URL}api/user/common/doctor-appointmentPdf/${selectedDoctor._id}`,
        {
          method: "POST",
          headers: {
          },
          body: formData,
        },
      );

      const data = await response.json();

      if (data.success) {
        toast.success(data.message);

        // // update UI locally
        const updatedList = doctors.map((doc) =>
          doc._id === selectedDoctor._id ? data.doctor : doc,
        );

        setDoctors(updatedList);
        setSelectedDoctor(data?.doctor);

        handleCloseSettings();
      } else {
        toast.error("Error: " + data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Unexpected error while updating doctor.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Grid container sx={{ height: "100vh" }}>
      {/* LEFT: canvas (9 cols) */}
      <Grid
        item
        xs={9}
        sx={{
          padding: 2,
          position: "relative",
          borderRight: "2px solid #ccc",
          overflowY: "auto",
        }}
      >
        {/* Canvas wrapper: its CSS size equals scaled viewport size.
            overlays are positioned inside wrapper in SCALED pixels (state * zoom).
            Dragging converts mouse positions back to unscaled coords. */}
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div style={{ fontSize: 13, color: "#444" }}>Zoom</div>
          <input
            aria-label="zoom"
            type="range"
            min={0.25}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => handleZoomChange(e.target.value)}
            style={{ width: 260 }}
          />
          <div style={{ minWidth: 60, textAlign: "right", fontSize: 13 }}>
            {Math.round(zoom * 100)}%
          </div>
          <div style={{ marginLeft: 12, fontSize: 12, color: "#666" }}>
            Snap: {SNAP_BASE}px (hold <b>Shift</b> for {SNAP_FINE}px)
          </div>
        </div>

        <div
          id="canvasWrapper"
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          style={{
            position: "relative",
            width: viewport ? `${viewport.width * zoom}px` : "auto",
            height: viewport ? `${viewport.height * zoom}px` : "auto",
            marginTop: 10,
            marginLeft: "auto",
            marginRight: "auto",
            background: viewport ? "#fff" : "#eee",
            // border: viewport ? "1px solid #ddd" : "1px dashed #ccc",
            boxShadow: viewport ? "rgb(0 0 0) 0px 0px 11px" : "none",
            cursor: dragging ? "grabbing" : "default",
            touchAction: "none",
          }}
        >
          {/* real canvas (internal pixels = viewport.width/height), visual size scaled via CSS */}
          <canvas
            id="pdf-canvas"
            style={{
              display: "block",
              // CSS size scaled by zoom — overlay positions must use (state * zoom)
              width: viewport ? `${viewport.width * zoom}px` : undefined,
              height: viewport ? `${viewport.height * zoom}px` : undefined,
            }}
          ></canvas>

          {/* Patient info overlay — spans full width; top position uses yValue * zoom */}
          {/* Patient info overlay */}
          {!viewport && <Typography>Choose a PDF</Typography>}
          {/* LEFT PATIENT BOX */}
          {viewport && (
            <div
              onMouseDown={(e) =>
                startDrag(e, "patientLeft", patientInfoLeftX, patientInfoLeftY)
              }
              style={{
                position: "absolute",
                left: `${patientInfoLeftX * zoom}px`,
                top: `${patientInfoLeftY * zoom}px`,
                width: `${180 * zoom}px`,
                height: `${40 * zoom}px`,
                background: "rgba(255,255,0,0.35)",
                cursor: "grab",
                // fontWeight: 600,
                // fontSize: 12 * zoom,
                userSelect: "none",
                /* ⭐ ADD THIS ⭐ */
                padding: 0,
                margin: 0,
                lineHeight: 1,
              }}
            >
              {/* COORD LABEL */}
              <div
                style={{
                  position: "absolute",
                  top: -23,
                  left: 0,
                  background: "black",
                  color: "white",
                  padding: "1px 4px",
                  fontSize: 10,
                  borderRadius: 3,
                  opacity: 0.8,
                }}
              >
                X: {patientInfoLeftX} • Y: {patientInfoLeftY}
              </div>
              <p
                style={{
                  margin: 0,
                  padding: 0,
                  fontWeight: 600,
                  fontSize: 12 * zoom,
                }}
              >
                PATIENT NAME
              </p>

              <p
                style={{
                  margin: 0,
                  marginTop: `${5 * zoom}px`,
                  padding: 0,
                  fontSize: `${9.75 * zoom}px`,
                }}
              >
                Sex/Age: Female / 18Y 10M 27D
              </p>

              <p
                style={{
                  margin: 0,
                  marginTop: `${5 * zoom}px`,
                  padding: 0,
                  fontSize: `${9.75 * zoom}px`,
                }}
              >
                Mobile: 1234567890
              </p>
            </div>
          )}

          {viewport && (
            <div
              onMouseDown={(e) =>
                startDrag(
                  e,
                  "patientRight",
                  patientInfoRightX,
                  patientInfoRightY,
                )
              }
              style={{
                position: "absolute",
                left: `${patientInfoRightX * zoom}px`,
                top: `${patientInfoRightY * zoom}px`,
                width: `${180 * zoom}px`,
                height: `${40 * zoom}px`,
                background: "rgba(17, 255, 0, 0.35)",
                cursor: "grab",
                // fontWeight: 600,
                // fontSize: 12 * zoom,
                userSelect: "none",
                /* ⭐ ADD THIS ⭐ */
                padding: 0,
                margin: 0,
                lineHeight: 1,
              }}
            >
              {/* COORD LABEL */}
              <div
                style={{
                  position: "absolute",
                  top: -23,
                  left: 0,
                  background: "black",
                  color: "white",
                  padding: "1px 4px",
                  fontSize: 10,
                  borderRadius: 3,
                  opacity: 0.8,
                }}
              >
                X: {patientInfoRightX} • Y: {patientInfoRightY}
              </div>
              <p
                style={{
                  margin: 0,
                  padding: 0,
                  fontWeight: 600,
                  fontSize: 12 * zoom,
                }}
              >
                UHID: OP123456
              </p>

              <p
                style={{
                  margin: 0,
                  marginTop: `${5 * zoom}px`,
                  padding: 0,
                  fontSize: `${9.75 * zoom}px`,
                }}
              >
                Appointment ID: AP123456
              </p>

              <p
                style={{
                  margin: 0,
                  marginTop: `${5 * zoom}px`,
                  padding: 0,
                  fontSize: `${9.75 * zoom}px`,
                }}
              >
                Appointment Date: 23-Nov-25
              </p>
            </div>
          )}

          {/* Doctor Info box: position scaled; size scaled */}
          {/* Doctor Info box */}
          {viewport && doctorInfo === "yes" && (
            <div
              onMouseDown={(e) => startDrag(e, "doctor", doctorX, doctorY)}
              style={{
                position: "absolute",
                left: `${doctorX * zoom}px`,
                top: `${doctorY * zoom}px`,
                width: `${250 * zoom}px`,
                height: `${106 * zoom}px`,
                background: "rgba(238, 0, 255, 0.16)",
                // border: `${1 * zoom}px dashed rgba(0,110,220,0.6)`,
                boxSizing: "border-box",
                cursor: "grab",
                userSelect: "none",
                color: "#064b7a",
                padding: 0,
                margin: 0,
                lineHeight: 1,
              }}
            >
              {/* 🔵 COORD LABEL */}
              <div
                style={{
                  position: "absolute",
                  top: -23,
                  left: 0,
                  background: "black",
                  color: "white",
                  padding: "1px 4px",
                  fontSize: 10,
                  borderRadius: 3,
                  opacity: 0.8,
                }}
              >
                X: {doctorX} • Y: {doctorY}
              </div>
              <p
                style={{
                  margin: 0,
                  padding: 0,
                  fontWeight: 800,
                  fontSize: 16 * zoom,
                }}
              >
                Dr. Suman Saikia
              </p>

              {Array.from({ length: 6 }).map((_, index) => (
                <p
                  key={index}
                  style={{
                    margin: 0,
                    marginTop: `${3 * zoom}px`,
                    padding: 0,
                    fontSize: `${12 * zoom}px`,
                    fontWeight: 600,
                  }}
                >
                  Here will be Qualification {index + 1}
                </p>
              ))}
            </div>
          )}

          {/* Doctor Note box */}
          {/* Doctor Note box */}
          {viewport && (doctorNote === "yes" || appNote === "yes") && (
            <div
              onMouseDown={(e) => startDrag(e, "note", noteX, noteY)}
              style={{
                position: "absolute",
                left: `${noteX * zoom}px`,
                top: `${noteY * zoom}px`,
                width: `${308 * zoom}px`,
                height: `${18 * zoom}px`,
                background: "rgba(255, 0, 43, 0.36)",
                // border: `${1 * zoom}px dashed rgba(0,160,80,0.6)`,
                boxSizing: "border-box",
                cursor: "grab",
                userSelect: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: Math.max(10, 12 * zoom),
                color: "#0b5a2b",
                fontWeight: 600,
              }}
            >
              {/* 🔵 COORD LABEL */}
              <div
                style={{
                  position: "absolute",
                  top: -23,
                  left: 0,
                  background: "black",
                  color: "white",
                  padding: "1px 4px",
                  fontSize: 10,
                  borderRadius: 3,
                  opacity: 0.8,
                }}
              >
                X: {noteX} • Y: {noteY}
              </div>
              Doctor Note
            </div>
          )}

          {/* MASTER PRESCRIPTION CONTAINER */}
          {viewport && (
            <div
              onMouseDown={(e) =>
                dragging !== "prescription-resize" &&
                startDrag(
                  e,
                  "prescription-container",
                  prescriptionBox.x,
                  prescriptionBox.y,
                )
              }
              style={{
                position: "absolute",
                left: `${prescriptionBox.x * zoom}px`,
                top: `${prescriptionBox.y * zoom}px`,
                width: `${prescriptionBox.width * zoom}px`,
                height: `${prescriptionBox.height * zoom}px`,
                background: "#fff0",
                border: "1px solid #1976d2",
                boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                display: "flex",
                flexDirection: "column",
                cursor:
                  dragging === "prescription-container" ? "grabbing" : "grab",
                overflow: "hidden",
              }}
            >
              {/* CONTENT */}
              <div
                style={{
                  flex: 1,
                  // padding: `${20 * zoom}px`,
                  display: "flex",
                  flexDirection: "column",
                  fontFamily: "Helvetica, Arial, sans-serif",
                  fontSize: `${11 * zoom}px`,
                  color: "#000",
                }}
              >
                {/* {showVitals && (
                  <>
                    <div style={{ marginBottom: `${18 * zoom}px` }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: `${13 * zoom}px`,
                          marginBottom: `${10 * zoom}px`,
                        }}
                      >
                        Vitals
                      </div>

                      <div style={{ display: "flex", gap: `${40 * zoom}px` }}>
                        <div>Height: 170 cm</div>
                        <div>Weight: 65 kg</div>
                        <div>Pulse: 72 bpm</div>
                        <div>BP: 120/80</div>
                      </div>
                    </div>

                    <div
                      style={{
                        height: 1,
                        background: "#ddd",
                        marginBottom: `${18 * zoom}px`,
                      }}
                    />
                  </>
                )}

                <div style={{ marginBottom: `${18 * zoom}px` }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: `${13 * zoom}px`,
                      marginBottom: `${8 * zoom}px`,
                    }}
                  >
                    Medical History
                  </div>

                  <div style={{ paddingLeft: `${10 * zoom}px` }}>
                    Hypertension since 5 years, Type 2 Diabetes, Previous knee
                    surgery, No known drug allergy, Occasional migraine episodes
                  </div>
                </div>

                <div
                  style={{
                    height: 1,
                    background: "#ddd",
                    marginBottom: `${18 * zoom}px`,
                  }}
                />

                <div style={{ marginBottom: `${18 * zoom}px` }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: `${13 * zoom}px`,
                      marginBottom: `${8 * zoom}px`,
                    }}
                  >
                    Investigations Needed
                  </div>

                  <div style={{ paddingLeft: `${10 * zoom}px` }}>
                    CBC, LFT, KFT, Thyroid Profile, Vitamin D, Blood Sugar,
                    Lipid Profile, Urine Routine
                  </div>
                </div>

                <div
                  style={{
                    height: 1,
                    background: "#ddd",
                    marginBottom: `${22 * zoom}px`,
                  }}
                />

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: `${13 * zoom}px`,
                      marginBottom: `${12 * zoom}px`,
                    }}
                  >
                    MEDICINES
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: `${10 * zoom}px`,
                      marginBottom: `${8 * zoom}px`,
                    }}
                  >
                    <div style={{ width: "45%" }}> </div>
                    <div style={{ width: "13%", textAlign: "center" }}>
                      Morning
                    </div>
                    <div style={{ width: "13%", textAlign: "center" }}>
                      Afternoon
                    </div>
                    <div style={{ width: "13%", textAlign: "center" }}>
                      Evening
                    </div>
                    <div style={{ width: "13%", textAlign: "center" }}>
                      Night
                    </div>
                  </div>

                  <div
                    style={{
                      height: 1,
                      background: "#000",
                      marginBottom: `${14 * zoom}px`,
                    }}
                  />

                  {[1, 2].map((i) => (
                    <div key={i} style={{ marginBottom: `${18 * zoom}px` }}>
                      <div style={{ display: "flex" }}>
                        <div style={{ width: "45%", fontWeight: 700 }}>
                          {i}. Paracetamol 500mg
                        </div>
                        <div style={{ width: "13%", textAlign: "center" }}>
                          1
                        </div>
                        <div style={{ width: "13%", textAlign: "center" }}>
                          -
                        </div>
                        <div style={{ width: "13%", textAlign: "center" }}>
                          1
                        </div>
                        <div style={{ width: "13%", textAlign: "center" }}>
                          -
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: `${6 * zoom}px`,
                          paddingLeft: `${15 * zoom}px`,
                          fontSize: `${10 * zoom}px`,
                          color: "#444",
                        }}
                      >
                        5 Days | After Food
                      </div>

                      <div
                        style={{
                          height: 1,
                          background: "#ddd",
                          marginTop: `${14 * zoom}px`,
                        }}
                      />
                    </div>
                  ))}
                </div> */}
              </div>

              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDragging("prescription-resize");
                  setOffset({ x: e.clientX, y: e.clientY });
                }}
                style={{
                  position: "absolute",
                  width: `${10 * zoom}px`,
                  height: `${10 * zoom}px`,
                  right: `${4 * zoom}px`,
                  bottom: `${4 * zoom}px`,
                  background: "#1976d2",
                  borderRadius: "3px",
                  cursor: "nwse-resize",
                }}
              />
            </div>
          )}
        </div>
      </Grid>

      {/* RIGHT: controls (3 cols) */}
      <Grid
        item
        xs={3}
        sx={{
          padding: 3,
          background: "#f7f9fc",
          borderLeft: "1px solid #e0e0e0",
          overflowY: "auto",
          //   maxHeight: "500px",
        }}
      >
        {selectedDoctor?.whichAppointmentTemplate === "doctor" ? (
          <Button
            variant="contained"
            component="label"
            startIcon={<ChangeCircleIcon />}
            sx={{
              textTransform: "none",
              background: "#1976d2",
              color: "white",
            }}
          >
            {selectedDoctor?.appointmentTemplate?.pdfUrl
              ? "Change Template"
              : "Select Template"}
            <input
              type="file"
              hidden
              accept="application/pdf"
              onChange={handlePdfUpload}
            />
          </Button>
        ) : (
          <Typography sx={{ fontSize: "11px", color: "green" }}>
            Using organization's default template
          </Typography>
        )}

        {/* Show upload ONLY if doctor has no PDF */}
        {/* {!selectedDoctor?.appointmentTemplate?.pdfUrl && (
          <input
            type="file"
            accept="application/pdf"
            onChange={handlePdfUpload}
            style={{ width: "100%" }}
          />
        )} */}

        {viewport && (
          <Typography sx={{ my: 1, fontSize: 12, color: "#666" }}>
            Drag the boxes on the PDF or edit these values. Hold <b>Shift</b>{" "}
            while dragging for fine snap.
          </Typography>
        )}
        <Box
          sx={{
            background: "white",
            padding: 2,
            borderRadius: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Patient Info (Left & Right)
          </Typography>

          {/* LEFT BOX INPUTS */}
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
            Left Box Coordinates
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <label style={{ fontSize: "12px", color: "#666" }}>
                Left X (px)
              </label>
              <input
                type="number"
                value={patientInfoLeftX}
                onChange={(e) => setPatientInfoLeftX(Number(e.target.value))}
                style={{
                  width: "100%",
                  height: "40px",
                  padding: "5px 8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  outline: "none",
                  fontSize: "14px",
                  background: "rgba(255,255,0,0.35)",
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <label style={{ fontSize: "12px", color: "#666" }}>
                Left Y (px)
              </label>
              <input
                type="number"
                value={patientInfoLeftY}
                onChange={(e) => setPatientInfoLeftY(Number(e.target.value))}
                style={{
                  width: "100%",
                  height: "40px",
                  padding: "5px 8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  outline: "none",
                  fontSize: "14px",
                  background: "rgba(255,255,0,0.35)",
                }}
              />
            </Box>
          </Box>

          {/* RIGHT BOX INPUTS */}
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
            Right Box Coordinates
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <label style={{ fontSize: "12px", color: "#666" }}>
                Right X (px)
              </label>
              <input
                type="number"
                value={patientInfoRightX}
                onChange={(e) => setPatientInfoRightX(Number(e.target.value))}
                style={{
                  width: "100%",
                  height: "40px",
                  padding: "5px 8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  outline: "none",
                  fontSize: "14px",
                  background: "rgba(17, 255, 0, 0.35)",
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <label style={{ fontSize: "12px", color: "#666" }}>
                Right Y (px)
              </label>
              <input
                type="number"
                value={patientInfoRightY}
                onChange={(e) => setPatientInfoRightY(Number(e.target.value))}
                style={{
                  width: "100%",
                  height: "40px",
                  padding: "5px 8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  outline: "none",
                  fontSize: "14px",
                  background: "rgba(17, 255, 0, 0.35)",
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            background: "white",
            padding: 2,
            borderRadius: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Doctor Info</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="doc"
                value="yes"
                checked={doctorInfo === "yes"}
                onChange={() => setDoctorInfo("yes")}
              />
              Yes
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="doc"
                value="no"
                checked={doctorInfo === "no"}
                onChange={() => setDoctorInfo("no")}
              />
              No
            </label>
          </Box>

          {doctorInfo === "yes" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <label style={{ fontSize: "12px", color: "#666" }}>
                  X (px)
                </label>
                <input
                  type="number"
                  value={doctorX}
                  onChange={(e) => setDoctorX(Number(e.target.value))}
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "5px 8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    outline: "none",
                    fontSize: "14px",
                    background: "rgba(238, 0, 255, 0.16)",
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <label style={{ fontSize: "12px", color: "#666" }}>
                  Y (px)
                </label>
                <input
                  type="number"
                  value={doctorY}
                  onChange={(e) => setDoctorY(Number(e.target.value))}
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "5px 8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    outline: "none",
                    fontSize: "14px",
                    background: "rgba(238, 0, 255, 0.16)",
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            background: "white",
            padding: 2,
            borderRadius: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Doctor Note</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="note"
                value="yes"
                checked={doctorNote === "yes"}
                onChange={() => setDoctorNote("yes")}
              />
              Yes
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="note"
                value="no"
                checked={doctorNote === "no"}
                onChange={() => setDoctorNote("no")}
              />
              No
            </label>
          </Box>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>App Note</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="appNote"
                value="yes"
                checked={appNote === "yes"}
                onChange={() => setAppNote("yes")}
              />
              Yes
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="appNote"
                value="no"
                checked={appNote === "no"}
                onChange={() => setAppNote("no")}
              />
              No
            </label>
          </Box>

          {(doctorNote === "yes" || appNote === "yes") && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <label style={{ fontSize: "12px", color: "#666" }}>
                  X (px)
                </label>
                <input
                  type="number"
                  value={noteX}
                  onChange={(e) => setNoteX(Number(e.target.value))}
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "5px 8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    outline: "none",
                    fontSize: "14px",
                    background: "rgba(255, 0, 43, 0.36)",
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <label style={{ fontSize: "12px", color: "#666" }}>
                  Y (px)
                </label>
                <input
                  type="number"
                  value={noteY}
                  onChange={(e) => setNoteY(Number(e.target.value))}
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "5px 8px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    outline: "none",
                    fontSize: "14px",
                    background: "rgba(255, 0, 43, 0.36)",
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            background: "white",
            padding: 2,
            borderRadius: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Prescription</Typography>

          {/* SHOW VITALS CHECKBOX */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <input
              type="checkbox"
              checked={showVitals}
              onChange={(e) => setShowVitals(e.target.checked)}
            />
            Show Patient Vitals
          </label>

          {/* POSITION INPUTS */}
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
            Position (px)
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <label style={{ fontSize: 12 }}>X</label>
              <input
                type="number"
                value={prescriptionBox.x}
                onChange={(e) =>
                  setPrescriptionBox((prev) => ({
                    ...prev,
                    x: Number(e.target.value),
                  }))
                }
                style={inputStyle}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <label style={{ fontSize: 12 }}>Y</label>
              <input
                type="number"
                value={prescriptionBox.y}
                onChange={(e) =>
                  setPrescriptionBox((prev) => ({
                    ...prev,
                    y: Number(e.target.value),
                  }))
                }
                style={inputStyle}
              />
            </Box>
          </Box>

          {/* SIZE INPUTS */}
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
            Size (px)
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <label style={{ fontSize: 12 }}>Width</label>
              <input
                type="number"
                value={prescriptionBox.width}
                onChange={(e) =>
                  setPrescriptionBox((prev) => ({
                    ...prev,
                    width: Number(e.target.value),
                  }))
                }
                style={inputStyle}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <label style={{ fontSize: 12 }}>Height</label>
              <input
                type="number"
                value={prescriptionBox.height}
                onChange={(e) =>
                  setPrescriptionBox((prev) => ({
                    ...prev,
                    height: Number(e.target.value),
                  }))
                }
                style={inputStyle}
              />
            </Box>
          </Box>
        </Box>
        <Button
          onClick={handleSubmitPdfSettings}
          variant="contained"
          color="success"
          disabled={loading}
        >
          {loading ? "Uploading..." : "Upload"}
        </Button>
      </Grid>
    </Grid>
  );
}
